import React, { useEffect, useMemo, useState } from 'react';
import Table, { TableSize } from '@components/Table';
import {
  duplicatePriceListPath,
  newPriceListPath,
  priceListPath,
  priceListsPath,
  dependenciesPriceListPath,
  archivePriceListPath
} from 'routes';
import api from '@utils/axios';
import { translate } from '@utils/i18n';
import toast from 'react-hot-toast';
import ActionButtons from './ActionButtons';
import ArchivePriceListModal from './ArchivePriceListModal';

const namespace = 'features.settings.price_lists.index';

const viewMode = {
  active: 'active',
  archived: 'archived'
};

function Index() {
  const currentHash = window.location.hash.replace('#', '');

  const [lists, setLists] = useState(null);
  const [view, setView] = useState(currentHash ? viewMode[currentHash] : viewMode.active);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [associatedItems, setAssociatedItems] = useState(null);

  useEffect(() => {
    const getPriceLists = async () => {
      const response = await api.get(`${priceListsPath()}.json`, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0'
        }
      });
      setLists(response.data);
    };

    getPriceLists();
  }, []);

  const activeItems = useMemo(() => (lists ?? []).filter((list) => list.active), [lists]);
  const inactiveItems = useMemo(() => (lists ?? []).filter((list) => !list.active), [lists]);

  const getAssociatedItems = async (priceList) => {
    const response = await api.get(dependenciesPriceListPath(priceList.id));
    const items = response.data.associatedItems;
    setAssociatedItems(items);

    return items;
  };

  const changeViewMode = () => {
    const baseURL = window.location.pathname;
    const fullURL = currentHash === 'archived' ? baseURL : `${baseURL}#archived`;
    window.history.pushState({}, '', fullURL);

    setView(view === viewMode.active ? viewMode.archived : viewMode.active);
  };

  const onConfirmModal = (replacePriceListId) => {
    onSwitchToArchive(selectedItem, replacePriceListId);
    setModalVisible(false);
  };

  const onArchive = async (priceList) => {
    setSelectedItem(priceList);
    const items = await getAssociatedItems(priceList);

    const hasDependencies = Object.values(items).some((item) => item > 0);

    if (hasDependencies || priceList.is_default) {
      setModalVisible(true);
    } else {
      onSwitchToArchive(priceList);
    }
  };

  const onSwitchToArchive = (priceList, replacePriceListId) => {
    api.post(archivePriceListPath(priceList.id), { price_list_destination_id: replacePriceListId }).then(() => {
      setLists((draft) => {
        const index = draft.findIndex((list) => list.id === priceList.id);
        draft[index].active = !draft[index].active;
        if (priceList.is_default) {
          draft[index].is_default = false;
          const indexOfNewDefault = draft.findIndex((list) => list.id === replacePriceListId);
          draft[indexOfNewDefault].is_default = true;
        }
        return [...draft];
      });
      toast.success(translate('archiving_successful', { namespace }));
    });
  };

  const onSwitchToUnarchive = (priceList) => {
    api.put(priceListPath(priceList.id), { price_list: { active: true } }).then(() =>
      setLists((draft) => {
        const index = draft.findIndex((list) => list.id === priceList.id);
        draft[index].active = !draft[index].active;
        return [...draft];
      })
    );
  };

  const columns = [
    {
      label: translate('price_list', { namespace }),
      dataKey: 'name',
      renderer: ({ item }) => (
        <>
          <a href={priceListPath(item.id)}>{item.name}</a>
          {item.is_default && (
            <button
              type="button"
              style={{
                borderRadius: '16px',
                height: '3.2rem',
                padding: '12px',
                backgroundColor: '#ededf2',
                marginLeft: '20px'
              }}
              className="qmb-control">
              {translate('default', { namespace: 'generic' })}
            </button>
          )}
        </>
      )
    },
    {
      label: '',
      align: 'left',
      dataKey: 'id',
      width: 70,
      renderer: ({ item }) => (
        <ActionButtons
          priceList={item}
          onArchive={() => onArchive(item)}
          onUnarchive={() => onSwitchToUnarchive(item)}
          onDuplicate={onDuplicate(item)}
        />
      )
    }
  ];

  const onDuplicate = (priceList) => {
    return () => {
      api
        .post(duplicatePriceListPath(priceList.id))
        .then((response) => {
          setLists(response.data.priceLists);
          toast.success(translate('duplicate_is_successful', { namespace }));
        })
        .catch(() => {
          toast.error(translate('duplicate_has_failed', { namespace }));
        });
    };
  };

  if (!lists) return null;

  return (
    <div className="workspace__body">
      <div className="workspace__major--listing">
        <div className="workspace__header">
          <div className="header__main">
            <h1 className="workspace__title">{translate('title', { namespace })}</h1>
          </div>
        </div>
        <div className="qmb-content-actions">
          <div className="content-actions__group">
            <a className="qmb-control" href={newPriceListPath()}>
              <i className="fa-light fa-circle-plus" />
              {translate('new', { namespace })}
            </a>
          </div>
          <div className="actions__group--seperate">
            <button type="button" className="qmb-control" onClick={() => changeViewMode()}>
              {view === viewMode.active
                ? translate('archived_view', { namespace })
                : translate('active_view', { namespace })}
            </button>
          </div>
          <ArchivePriceListModal
            visible={modalVisible}
            onCloseModal={() => setModalVisible(false)}
            priceListForSelect={activeItems}
            priceList={selectedItem}
            associatedItems={associatedItems}
            onConfirm={onConfirmModal}
          />
        </div>
        <div>
          {lists.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: '25px', color: '#1967D2' }}>
              {translate('no_data', { namespace })}
            </div>
          )}
          {lists.length > 0 && (
            <Table
              columns={columns}
              data={view === viewMode.active ? activeItems : inactiveItems}
              size={TableSize.Full}
              variant="listing"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Index;
